import { type FC, useEffect, useMemo, useState } from 'react';

import ArrowCircleLeft from '@/components/Icons/ArrowCircleLeft';
import XCircle from '@/components/Icons/XCircle';
import Modal from '@/components/Modal';
import { ToastTypeVariants } from '@/context/ToastContext';
import removeNullish from '@/helpers/removeNullish';
import { useToast } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import useUserWorkflowContext from '@/hooks/useUserWorkflowContext';
import * as tsClient from '@/tsClient';
import { type WorkflowOwner } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import { ESignatureRequestStatus, MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';

import ESignatureCollaboratorsSelection from './ESignatureCollaboratorsSelection';
import ESignatureFileSelection from './ESignatureFileSelection';
import s from './ESignatureModal.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (args: { eSignatureRequestId: string; eSignatureRequestStatus: ESignatureRequestStatus }) => void;
  eSignatureRequestId: string;
  eSignatureRequestStatus: ESignatureRequestStatus;
  reloadWorkflow?: () => Promise<void>;
};

const ESignatureUploadModal: FC<Props> = ({
  isOpen,
  onClose,
  onContinue,
  eSignatureRequestId,
  eSignatureRequestStatus,
  reloadWorkflow,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const { collaborators } = useUserWorkflowContext();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const addToast = useToast();
  const {
    getData: handleFileAndSignersFn,
    fetchingState: { isFetching: isUpdatingESignatureRequest },
  } = useNoodleApi(tsClient.eSignatureRequests.handleFileAndSigners, {
    toastErrorMessage: (error) => [
      ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'Error updating signature request. Please try again.',
    ],
    toastOnError: true,
    toastSuccessMessage: () => [ToastTypeVariants.SUCCESS, 'Signature request updated successfully!'],
  });
  const NUM_STEPS = 2;

  const modalTitle = useMemo(() => {
    if (currentStep === 0) {
      return 'Document Signature Request Details';
    }
    return 'Assign Signers';
  }, [currentStep]);

  const onNextStep = (): void => {
    if (currentStep === 0) {
      setCurrentStep(1);
    }
  };

  const handleOnClose = (): void => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  };

  const onSubmit = async (owners: WorkflowOwner[]): Promise<void> => {
    if (!file) {
      addToast(ToastTypeVariants.ERROR, 'No File Selected');
      return;
    }
    const fullOwners = owners
      .map((owner) => {
        const person = collaborators.find((collaborator) => collaborator.personId === owner.personId);
        if (!person) {
          return null;
        }
        return {
          email: person.email,
          name: person.name,
          personId: person.personId,
        };
      })
      .filter(removeNullish);

    const fileBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const { result } = reader;
        const base64 = (result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    await handleFileAndSignersFn({
      eSignatureRequestId,
      fileBase64,
      fileName: file?.name,
      signers: fullOwners.map((owner) => ({
        acceptEachField: true,
        email: owner.email!,
        enableEmails: false,
        id: owner.personId,
        name: owner.name!,
        routingOrder: 1,
        showFinishSigningButton: true,
        signerType: 'embedded',
      })),
    });
    reloadWorkflow?.();
  };

  useEffect(() => {
    if (eSignatureRequestStatus === ESignatureRequestStatus.DRAFT) {
      onContinue({ eSignatureRequestId, eSignatureRequestStatus });
      onClose();
    }
  }, [eSignatureRequestId, eSignatureRequestStatus]);

  if (!isOpen) return null;

  return (
    <Modal
      title={modalTitle}
      onClose={handleOnClose}
      closeIcon={
        currentStep === 0 || currentStep === NUM_STEPS ? (
          <XCircle weight='fill' size={24} color='var(--color-gray-100)' />
        ) : (
          <ArrowCircleLeft weight='fill' size={24} color='var(--color-gray-100)' />
        )
      }
    >
      <div className={s.modalContent}>
        {currentStep === 0 && <ESignatureFileSelection file={file} setFile={setFile} onNextStep={onNextStep} />}
        {currentStep === 1 && (
          <ESignatureCollaboratorsSelection
            attachmentReferenceId={eSignatureRequestId}
            attachmentType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST}
            collaborators={collaborators}
            handleCollaboratorAssignment={onSubmit}
            isSubmitting={isUpdatingESignatureRequest}
          />
        )}
      </div>
    </Modal>
  );
};

export default ESignatureUploadModal;
